import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppUpdateService } from '@core/services/app-update.service';
import { AuthService } from '@core/services/auth.service';
import { MaintenanceNavigationService } from '@core/services/maintenance-navigation.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { UrlService } from '@core/services/url.service';
import { CaseConverter } from '@core/utils/case-converter';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { UuidUtilitiesService } from '@utilities/uuid-utilities.service';
import { get } from 'lodash';
import { EMPTY, Observable, catchError, switchMap, throwError } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { IApiError, IApiErrorDetail } from '../interfaces/api-error.interface';
import { ErrorFormatterService } from '../services/error-formatter.service';

const NON_VISUAL_ERRORS = ['ERR_1003'];
const NON_SNACKBAR_ERROR_KEYS = [
  'apiErrorMessages.alreadyExists.channel',
  'apiErrorMessages.alreadyExists.creatorChannel',
];
// /brief/analyze errors are translated by the create-campaign dialog's store instead.
const NON_SNACKBAR_URLS = ['/invitations/accept/', '/campaign-analytics/export/', '/brief/analyze'];
const NON_SNACKBAR_ERROR_CODES = [
  'EMAIL_USED_FOR_DIFFERENT_CREATOR',
  'ALREADY_INVITED',
  'SELF_INVITATION_FORBIDDEN',
  'EMAIL_USED_IN_DIFFERENT_GROUP',
  'game_not_found',
  'forbidden_deliverable',
  'customer_name_already_exists',
  'creator_name_already_exists',
];

// Caps how often a 429 for the same endpoint shape gets reported to Sentry - a rate-limit
// storm (e.g. a polling loop hammering one endpoint) would otherwise create one event per
// request instead of one per episode.
const RATE_LIMIT_REPORT_INTERVAL_MS = 60000;

// Hoisted so normalizeUrlForReporting doesn't recompile the same pattern on every call.
const UUID_PATH_REGEX = new RegExp(UuidUtilitiesService.UUID_V4_REGEX.source, 'gi');

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly appUpdateService = inject(AppUpdateService);
  private readonly maintenanceNavigationService = inject(MaintenanceNavigationService);
  private readonly lastRateLimitReportAt = new Map<string, number>();

  constructor(
    private readonly urlService: UrlService,
    private readonly snackBarService: SnackbarService,
    private readonly translate: TranslateService,
    private readonly errorFormatterService: ErrorFormatterService,
  ) {}

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const url = this.urlService.buildApiUrl(req.url);

    if (/(^|\/)i18n\/.*\.json(\?.*)?$/.test(url)) {
      return next.handle(
        req.clone({
          withCredentials: true,
        }),
      );
    }

    let convertedRequestBody: unknown = req.body;

    if (!(req.body instanceof FormData) && req.body) {
      convertedRequestBody = CaseConverter.deeplyConvertKeys(req.body, CaseConverter.toSnakeCase);
    }

    const apiReq = req.clone({
      body: convertedRequestBody,
      url,
      withCredentials: true,
      ...(AuthService.currentDbUser?.campaignVersion
        ? {
            setHeaders: {
              'campaign-version': AuthService.currentDbUser?.campaignVersion,
            },
          }
        : {}),
    });

    if (this.isAuthorizationExemptPath(url, req.method)) {
      return this.handleResponse(apiReq, next);
    }

    return this.handleAuthenticatedRequest(apiReq, next);
  }

  private handleAuthenticatedRequest(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.authService.getIdToken().pipe(
      take(1),
      switchMap((token) => {
        if (token) {
          return this.handleResponse(this.createAuthenticatedRequest(req, token), next);
        }

        return this.authService.refreshToken().pipe(
          switchMap((refreshedToken) => {
            if (!refreshedToken) {
              return throwError(() => new Error('No token available'));
            }

            return this.handleResponse(this.createAuthenticatedRequest(req, refreshedToken), next);
          }),
          catchError(() => throwError(() => new Error('No token available'))),
        );
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.message === 'No token available') {
          this.logLoginRedirectEndpoint(req, 'No token available');
          // This redirect ends the session without going through AuthService.signOut() (there's
          // no Firebase session left to sign out of at this point) - see
          // AppUpdateService.clearImageDataGroupCache for why a session end needs this regardless
          // of which code path triggered it.
          void this.appUpdateService.clearImageDataGroupCache();
          this.router.navigate(['/auth/login'], {
            queryParams: { returnUrl: this.router.url },
          });
        }

        return throwError(() => error);
      }),
    );
  }

  private handleResponse(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      map((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          if (
            req.url.includes('/images/') ||
            (req.url.includes('/files/') && !this.isJsonFileEndpoint(req.url)) ||
            this.isAnalyticsImageEndpoint(req.url)
          ) {
            return event;
          }

          const convertedResponseBody = CaseConverter.deeplyConvertKeys(event.body, CaseConverter.toCamelCase);

          return event.clone({ body: convertedResponseBody });
        }

        return event;
      }),
      catchError((error: Omit<HttpErrorResponse, 'error'> & { error: IApiError }) => {
        if (error.status === 503 || (error.status >= 500 && req.url.includes('/health'))) {
          this.maintenanceNavigationService.navigateToMaintenance();

          return EMPTY;
        }

        if (error.status === 401) {
          const details = Array.isArray(error.error?.details) ? error.error?.details : [error.error?.details];

          if (details[0]?.code === 'user_inactive') {
            this.authService.signOut();
            this.handleErrorMessage(error);

            return throwError(() => error);
          }

          return this.authService.refreshToken().pipe(
            switchMap(() => this.authService.getIdToken()),
            switchMap((token) => {
              if (!token) {
                return throwError(() => new Error('No token available after refresh'));
              }

              const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${token}` },
              });

              return this.handleResponse(retryReq, next);
            }),
            catchError(() => {
              this.logLoginRedirectEndpoint(req, '401 after token refresh');
              // Same reasoning as the "No token available" redirect above.
              void this.appUpdateService.clearImageDataGroupCache();
              this.router.navigate(['/auth/login'], {
                queryParams: { returnUrl: this.router.url },
              });

              return throwError(() => error);
            }),
          );
        }

        if (error.status === 429) {
          this.reportRateLimited(req);

          return throwError(() => error);
        }

        this.handleErrorMessage(error);

        return throwError(() => error);
      }),
    );
  }

  private reportRateLimited(req: HttpRequest<unknown>): void {
    const normalizedUrl = this.normalizeUrlForReporting(req.url);
    const endpointKey = `${req.method} ${normalizedUrl}`;
    const now = Date.now();
    const lastReportedAt = this.lastRateLimitReportAt.get(endpointKey);

    if (lastReportedAt && now - lastReportedAt < RATE_LIMIT_REPORT_INTERVAL_MS) {
      return;
    }

    this.pruneStaleRateLimitReports(now);
    this.lastRateLimitReportAt.set(endpointKey, now);

    Sentry.withScope((scope) => {
      scope.setFingerprint(['api-429', req.method, normalizedUrl]);
      scope.setTag('http.status_code', 429);
      scope.setTag('http.method', req.method);
      // Normalized, not req.url - keeps real campaign/creator IDs out of Sentry instead of
      // undoing the grouping normalizeUrlForReporting exists for.
      scope.setExtra('url', normalizedUrl);
      Sentry.captureMessage('API rate limited (429)', 'warning');
    });
  }

  // lastRateLimitReportAt is a singleton map on an interceptor that lives for the whole app
  // session - without this, it would only ever grow. An entry past the report interval can
  // never block a future report anyway, so it's safe to drop here.
  private pruneStaleRateLimitReports(now: number): void {
    for (const [key, reportedAt] of this.lastRateLimitReportAt) {
      if (now - reportedAt >= RATE_LIMIT_REPORT_INTERVAL_MS) {
        this.lastRateLimitReportAt.delete(key);
      }
    }
  }

  // Swaps UUID path segments for `:id` so requests to the same endpoint shape (e.g.
  // GET /campaigns/{id}/analytics) group into one Sentry issue instead of one per record.
  private normalizeUrlForReporting(url: string): string {
    return url.replace(UUID_PATH_REGEX, ':id');
  }

  private createAuthenticatedRequest(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private isAuthorizationExemptPath(url: string, method: string): boolean {
    const uuidRegex = UuidUtilitiesService.UUID_V4_REGEX.source;
    const exemptPaths: (string | RegExp | { path: RegExp; methods: string[] })[] = [
      '/campaigns/briefing/creator/',
      '/campaign-analytics/briefing/creator/',
      '/campaign-analytics/creator-analytics-slices/export/',
      '/campaign-analytics/creator-analytics-slices/briefing/creator/',
      '/campaign-analytics/creator-analytics-slices/cta-kpi-status/briefing/creator/',
      '/campaign-analytics/creator-analytics-slices/cta-kpi-status/export/',
      '/campaign-analytics/creator-analytics-slices/matched-kpis/export/',
      '/creator-analytics/campaigns/export/',
      '/platform-feature-kpis/performance/export/',
      '/auth/login',
      '/images/briefing/',
      '/images/export/',
      '/files/briefing/',
      '/files/export/',
      '/files/social-content-files/briefing/',
      '/invitations/accept/',
      '/campaigns/export/',
      '/campaign-analytics/export/',
      '/manual-finding-status/export/',
      '/manual-deliverable-status/export/',
      '/manual-deliverable-status/briefing/creator/',
      '/manual-matching-status/export/',
      '/manual-matching-status/briefing/creator/',
      '/findings/export/',
      '/users/forgot-password',
      '/events/briefing/',
      '/events/export/',
      new RegExp(`\\/campaign-exports\\/${uuidRegex}$`),
      new RegExp(`\\/campaign-analytics\\/${uuidRegex}\\/${uuidRegex}$`),
      new RegExp(`\\/materials\\/export\\/${uuidRegex}$`),
      {
        methods: ['DELETE'],
        path: new RegExp(`\\/files\\/social-content-files\\/${uuidRegex}\\/briefing\\/${uuidRegex}$`),
      },
      { methods: ['POST'], path: /\/files\/social-contents\/[^/]+\/briefing\/[^/]+$/ },
      // GET .../social-content-files/{id}/briefing/{briefingId}/metadata - added in
      // CampaignManagementAPI@7bad073 alongside the DELETE sibling above. Public/token-less on
      // the backend (no get_current_user dependency, and whitelisted in UserMiddleware's
      // public-route regex - see middlewares.py) since a creator viewing their briefing isn't
      // logged in. Without this entry the interceptor tries to attach a Firebase token before
      // sending the request, finds none, and redirects to /auth/login before the request ever
      // reaches the backend.
      new RegExp(`\\/files\\/social-content-files\\/${uuidRegex}\\/briefing\\/${uuidRegex}\\/metadata$`),
    ];

    return exemptPaths.some((path) => {
      if (typeof path === 'string') {
        return url.includes(path);
      }

      if (path instanceof RegExp) {
        return path.test(url) && method === 'GET';
      }

      return path.path.test(url) && path.methods.includes(method);
    });
  }

  // Everything under /images/ and most of /files/ is a raw byte GET (Response body is the
  // file's bytes, not JSON) so must be left alone by the camelCase conversion below - converting
  // would try to walk a Blob/ArrayBuffer as if it were a plain object. The exceptions are the
  // JSON endpoints that live under those same path prefixes: the social-content-files list/
  // upload endpoints (always returned JSON), and every dedicated `/metadata` endpoint added in
  // CampaignManagementAPI@7bad073 (own/admin/briefing/export file metadata, plus the
  // social-content-file-specific ones) - those return a FileSchema/SocialContentFile JSON body
  // and need the same camelCase treatment as every other API response.
  private isJsonFileEndpoint(url: string): boolean {
    return (
      url.includes('/files/social-content-files') ||
      url.includes('/files/social-contents/') ||
      /\/metadata(\?|$)/.test(url)
    );
  }

  // Blob endpoint: camelCasing would turn the Blob into `{}`; a 404 (no image) falls back to a placeholder.
  private isAnalyticsImageEndpoint(url: string): boolean {
    return /\/findings\/.+\/analytics-image(\?|$)/.test(url);
  }

  private isSocialContentFileUploadEndpoint(url: string): boolean {
    return url.includes('/files/social-contents/');
  }

  private handleErrorMessage(httpErrorResponse: HttpErrorResponse): void {
    const errorUrl = httpErrorResponse.url ?? '';

    if (
      errorUrl.includes('/images/') ||
      (errorUrl.includes('/files/') && !this.isJsonFileEndpoint(errorUrl)) ||
      this.isSocialContentFileUploadEndpoint(errorUrl) ||
      this.isAnalyticsImageEndpoint(errorUrl) ||
      (httpErrorResponse.error as { detail?: string })?.detail === 'Not Found'
    ) {
      return;
    }

    try {
      const error = httpErrorResponse.error as IApiError;

      if (
        NON_VISUAL_ERRORS.includes(get(error, 'error_code', '')) ||
        NON_SNACKBAR_URLS.some((url) => httpErrorResponse.url?.includes(url))
      ) {
        return;
      }

      const details = this.errorFormatterService.getErrorDetails(error);

      if (details.length) {
        details.forEach((detail: IApiErrorDetail) => {
          const { message, translationKey } = this.errorFormatterService.getErrorMessage(detail, error);
          const userGroupSwitchedAfterEndDateReadReached = get(error, 'debug_info.auto_switched_user_group', false);

          if (
            !this.isNonVisibleProductionError(message) &&
            !NON_SNACKBAR_ERROR_KEYS.includes(translationKey) &&
            !NON_SNACKBAR_ERROR_CODES.includes(detail.code) &&
            !NON_SNACKBAR_URLS.some((url) => httpErrorResponse.url?.includes(url))
          ) {
            this.snackBarService.openError(message);
          }

          if (detail.code === 'END_DATE_READ_REACHED' && !userGroupSwitchedAfterEndDateReadReached) {
            this.authService.signOut();
          }

          if (userGroupSwitchedAfterEndDateReadReached) {
            setTimeout(() => {
              void this.router.navigate(['/dashboard']);
            }, 5000);
          }
        });
      } else {
        this.openFallbackErrorMessage(error.message);
      }
    } catch {
      this.openFallbackErrorMessage();
    }
  }

  private openFallbackErrorMessage(errorMessage?: string): void {
    const unknownMessage = this.translate.instant('apiErrorMessages.unknown');

    if (this.isNonVisibleProductionError(errorMessage)) {
      return;
    }

    this.snackBarService.openError(errorMessage ?? unknownMessage);
  }

  private logLoginRedirectEndpoint(req: HttpRequest<unknown>, reason: string): void {
    if (environment.production) {
      return;
    }

    // eslint-disable-next-line no-console
    console.error('[ApiInterceptor] Redirecting to login due to endpoint:', {
      method: req.method,
      reason,
      url: req.url,
    });
  }

  private isNonVisibleProductionError(message?: string): boolean {
    const unknownMessage = this.translate.instant('apiErrorMessages.unknown');

    return environment.production && (!message || message === unknownMessage);
  }
}
